import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import API from "../api/axios";
import "../styles/auth.css";

const styles = `
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  .vs-auth-step {
    font-size: 11px;
    font-weight: 700;
    color: var(--accent);
    letter-spacing: 1.5px;
    text-transform: uppercase;
    margin-bottom: 18px;
  }
  .vs-auth-pw-hint {
    font-size: 12px;
    color: var(--accent);
    margin-bottom: 14px;
    line-height: 1.6;
    padding: 10px 14px;
    border: 1px solid var(--border);
    border-radius: 10px;
    background: var(--accent-tint);
  }

`;

function ForgotPassword() {
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { t } = useTranslation();

  useEffect(() => {
    const el = document.createElement("style");
    el.textContent = styles;
    document.head.appendChild(el);
    return () => document.head.removeChild(el);
  }, []);

  const handleSendOTP = async () => {
    if (!email) { toast.error("Please enter your email"); return; }
    try {
      setLoading(true);
      await API.post("/auth/forgot-password", { email });
      toast.success("OTP sent to your email 📧");
      setStep(2);
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to send OTP");
    } finally { setLoading(false); }
  };

  const handleResetPassword = async () => {
    if (!otp || !newPassword || !confirmPassword) { toast.error("Please fill in all fields"); return; }
    if (newPassword !== confirmPassword) { toast.error("Passwords do not match"); return; }
    try {
      setLoading(true);
      await API.post("/auth/reset-password", { email, otp, newPassword });
      toast.success("Password reset successfully! Please login ✅");
      navigate("/");
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to reset password");
    } finally { setLoading(false); }
  };

  return (
    <div className="vs-auth-page">
      <div className="vs-auth-card">
        <h1 className="vs-auth-logo">VaultSphere</h1>
        <p className="vs-auth-tagline">{t("fp_tagline")}</p>

        <div className="vs-auth-divider">
          <div className="vs-auth-divider-line" />
          <div className="vs-auth-divider-dot" />
          <div className="vs-auth-divider-line" />
        </div>

        {step === 1 ? (
          <>
            <p className="vs-auth-step">{t("fp_step1")}</p>
            <input
              className="vs-auth-input"
              type="email"
              placeholder={t("fp_emailPlaceholder")}
              value={email}
              onChange={e => setEmail(e.target.value)}
              onKeyPress={e => e.key === "Enter" && handleSendOTP()}
            />
            <button className="vs-auth-btn" onClick={handleSendOTP} disabled={loading}>
              {loading ? t("fp_sendingOtp") : t("fp_sendResetOtp")}
            </button>
          </>
        ) : (
          <>
            <p className="vs-auth-step">{t("fp_step2")}</p>
            <input
              className="vs-auth-otp-input"
              type="text"
              placeholder="······"
              value={otp}
              onChange={e => setOtp(e.target.value)}
              maxLength={6}
            />
            <p className="vs-auth-hint">{t("otpExpiry")}</p>
            <input className="vs-auth-input" type="password" placeholder={t("fp_newPasswordPlaceholder")} value={newPassword} onChange={e => setNewPassword(e.target.value)} />
            <input className="vs-auth-input" type="password" placeholder={t("fp_confirmPasswordPlaceholder")} value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} />
            <p className="vs-auth-pw-hint">{t("passwordHint")}</p>
            <button className="vs-auth-btn" onClick={handleResetPassword} disabled={loading}>
              {loading ? t("fp_resetting") : t("fp_resetPassword")}
            </button>
          </>
        )}

        <p className="vs-auth-footer">
          {t("fp_rememberPassword")}{" "}
          <Link to="/">{t("fp_loginHere")}</Link>
        </p>
      </div>
    </div>
  );
}

export default ForgotPassword;